import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, homePath } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(homePath(u.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-sapphire text-white p-12">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 bg-ocean flex items-center justify-center rounded-md"><Sparkles className="w-5 h-5" /></div>
          <span className="font-heading font-extrabold text-xl">Clean Slate Connect</span>
        </Link>
        <div>
          <h1 className="font-heading font-black text-4xl tracking-tight leading-tight">Turnovers, handled.</h1>
          <p className="text-white/60 mt-4 max-w-md">Schedule, dispatch, chat and pay — the tactical dashboard for turnover cleaning.</p>
        </div>
        <span className="text-white/40 text-sm">© 2026 Clean Slate Connect. Owned and operated by Clean Slate Biohazard Response Team LLC.</span>
      </div>

      <div className="flex items-center justify-center p-6 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h2 className="font-heading font-extrabold text-3xl tracking-tight text-sapphire mb-1">Log in</h2>
          <p className="text-muted-foreground text-sm mb-8">Welcome back to Clean Slate Connect.</p>

          {error && <div className="mb-4 text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded" data-testid="login-error">{error}</div>}

          <div className="space-y-4">
            <div>
              <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" placeholder="••••••••" />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full mt-6 bg-ocean hover:bg-ocean/90" data-testid="login-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log In"}
          </Button>
          <p className="text-sm text-center mt-3"><Link to="/forgot-password" className="text-muted-foreground hover:text-ocean" data-testid="link-forgot">Forgot password?</Link></p>

          <p className="text-sm text-center mt-6 text-muted-foreground">
            No account? <Link to="/register" className="text-ocean font-semibold" data-testid="link-register">Sign up</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
